'use client'

/**
 * SpotlightOverlay — visual UI highlight that follows the bot's instructions.
 *
 * Render strategy: a single fixed-positioned div drawn via React portal at
 * document.body. The dim backdrop is achieved with a HUGE box-shadow on the
 * target's outline rect (no SVG masking).
 *
 * Action semantics:
 *   - 'click'   -> one-shot click listener (document-level, capture phase)
 *   - 'input'   -> no auto-advance; user clicks "Next"
 *   - 'focus'   -> focusin listener
 *   - 'observe' -> no DOM listener; user clicks "Next"
 *   - 'wait'    -> auto-advance after wait_ms (default 1500)
 */

import { useEffect, useState, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'

const PAD = 8
const CALLOUT_GAP = 12
const CALLOUT_WIDTH = 320
const CALLOUT_HEIGHT_EST = 320

// True when less than ~60% of the element's visible band remains on screen —
// either clipped by a scrollable ancestor or outside the viewport. Checks
// BOTH axes: phone drawers hide by sliding off-screen HORIZONTALLY, so a
// vertical-only check reports their menu rows as visible (owner-reported,
// 11 Aug — the guide pointed at rows inside the closed drawer).
function isClippedFromView(el, r) {
  const minBandY = Math.min(r.height, 24) * 0.6
  const minBandX = Math.min(r.width, 24) * 0.6
  let node = el.parentElement
  while (node && node !== document.body) {
    const cs = window.getComputedStyle(node)
    if (/(auto|scroll|hidden)/.test(cs.overflowY + ' ' + cs.overflowX)) {
      const cr = node.getBoundingClientRect()
      const visibleY = Math.min(r.bottom, cr.bottom) - Math.max(r.top, cr.top)
      const visibleX = Math.min(r.right, cr.right) - Math.max(r.left, cr.left)
      if (visibleY < minBandY || visibleX < minBandX) return true
    }
    node = node.parentElement
  }
  const visibleInViewportY = Math.min(r.bottom, window.innerHeight) - Math.max(r.top, 0)
  const visibleInViewportX = Math.min(r.right, window.innerWidth) - Math.max(r.left, 0)
  return visibleInViewportY < minBandY || visibleInViewportX < minBandX
}

export function SpotlightOverlay({ step, totalSteps, onAdvance, onSkip, onPrev, onStop, theme, onTargetMissing }) {
  const [targetEl, setTargetEl] = useState(null)
  const [rect, setRect] = useState(null)
  const [missing, setMissing] = useState(false)
  const [searching, setSearching] = useState(false)
  const [mounted, setMounted] = useState(false)
  const measureTimerRef = useRef(null)

  const [pausedByHost, setPausedByHost] = useState(false)

  useEffect(() => { setMounted(true) }, [])

  // Host pause (body[data-ashraya-paused]): instead of vanishing entirely we
  // show a small "I'm waiting" card above the host's modal so the user knows
  // the guide continues once they close it.
  useEffect(() => {
    const check = () => setPausedByHost(document.body.dataset.ashrayaPaused === 'true')
    check()
    const mo = new MutationObserver(check)
    mo.observe(document.body, { attributes: true, attributeFilter: ['data-ashraya-paused'] })
    return () => mo.disconnect()
  }, [])

  const measureTarget = useCallback(() => {
    if (!step?.target_id) {
      setTargetEl(null); setRect(null); setMissing(false)
      return false
    }
    const el = document.querySelector(`[data-tour-id="${step.target_id}"]`)
    if (!el) {
      setTargetEl(null); setRect(null)
      return false
    }
    const probeRect = el.getBoundingClientRect()
    if (probeRect.width === 0 || probeRect.height === 0) {
      setTargetEl(null); setRect(null)
      return false
    }
    const cs = window.getComputedStyle(el)
    if (cs.display === 'none' || cs.visibility === 'hidden' || cs.opacity === '0') {
      setTargetEl(null); setRect(null)
      return false
    }
    // A target scrolled out of view inside a drawer/menu still reports a
    // rect — drawing the ring there would highlight whatever OTHER element
    // happens to sit at those coordinates (owner-reported: the ring landed
    // on the Install button while the caption said 'Lines of business').
    // Treat a mostly-clipped target as not-found so the scroll retries run.
    if (isClippedFromView(el, probeRect)) {
      setTargetEl(null); setRect(null)
      return false
    }
    setTargetEl(el)
    setMissing(false)
    setRect({ top: probeRect.top, left: probeRect.left, width: probeRect.width, height: probeRect.height })
    return true
  }, [step?.target_id])

  // Standing watch (11 Aug fix): the old code glanced at the target ONCE,
  // 4s into the step — whatever the screen looked like at that instant
  // decided the step's fate forever. Now we check every 900ms for the whole
  // life of the step: if the target stays out of human view we flag missing
  // (which asks the host to open whatever hides it, e.g. the phone drawer)
  // and we keep re-asking, so a late sheet-close can never strand the step.
  useEffect(() => {
    if (!step?.target_id) return
    setMissing(false)
    let invisibleTicks = 0
    const tick = () => {
      const el = document.querySelector(`[data-tour-id="${step.target_id}"]`)
      const r = el?.getBoundingClientRect()
      let visible = false
      if (el && r && r.width > 0 && r.height > 0) {
        const cs = window.getComputedStyle(el)
        visible = cs.display !== 'none' && cs.visibility !== 'hidden' && cs.opacity !== '0' &&
          !isClippedFromView(el, r)
      }
      if (visible) {
        invisibleTicks = 0
        measureTarget()
        return
      }
      invisibleTicks += 1
      if (invisibleTicks === 3) {
        setMissing(true)
        if (typeof onTargetMissing === 'function') onTargetMissing(step)
      } else if (invisibleTicks > 3 && (invisibleTicks - 3) % 5 === 0) {
        if (typeof onTargetMissing === 'function') onTargetMissing(step)
      }
    }
    const interval = setInterval(tick, 900)
    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step?.step_number, step?.target_id, measureTarget, onTargetMissing])

  useEffect(() => {
    if (!step?.target_id || targetEl || missing) {
      setSearching(false)
      return
    }
    const t = setTimeout(() => setSearching(true), 200)
    return () => clearTimeout(t)
  }, [step?.step_number, step?.target_id, targetEl, missing])

  useEffect(() => {
    if (!step?.target_id) return
    measureTarget()
    const timers = []
    const attemptScroll = () => {
      const el = document.querySelector(`[data-tour-id="${step.target_id}"]`)
      if (el && step.scroll_to !== false) {
        try {
          el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' })
        } catch {}
      }
      timers.push(setTimeout(measureTarget, 350))
    }
    // Drawers/accordions animate open on phones — a single early scroll can
    // land mid-slide and leave the target clipped. Scroll once early, then
    // re-check after the transitions have settled and retry if still hidden.
    timers.push(setTimeout(attemptScroll, 60))
    timers.push(setTimeout(() => { if (!measureTarget()) attemptScroll() }, 500))
    timers.push(setTimeout(() => { if (!measureTarget()) attemptScroll() }, 1100))
    return () => timers.forEach(clearTimeout)
  }, [step?.step_number, step?.target_id, step?.scroll_to, measureTarget])

  useEffect(() => {
    if (typeof window === 'undefined') return
    let raf = null
    const onChange = () => {
      if (raf) cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => { measureTarget(); raf = null })
    }
    window.addEventListener('resize', onChange)
    window.addEventListener('scroll', onChange, { capture: true, passive: true })
    return () => {
      window.removeEventListener('resize', onChange)
      window.removeEventListener('scroll', onChange, { capture: true })
      if (raf) cancelAnimationFrame(raf)
    }
  }, [measureTarget])

  useEffect(() => {
    if (typeof document === 'undefined') return
    document.body.dataset.ashraya = 'active'
    return () => {
      delete document.body.dataset.ashraya
    }
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined' || !step?.target_id) return
    let debounceTimer = null
    let raf = null
    const schedule = () => {
      if (debounceTimer) clearTimeout(debounceTimer)
      debounceTimer = setTimeout(() => {
        if (raf) cancelAnimationFrame(raf)
        raf = requestAnimationFrame(() => { measureTarget(); raf = null })
      }, 80)
    }
    const observer = new MutationObserver(schedule)
    observer.observe(document.body, { childList: true, subtree: true, attributes: false })
    return () => {
      observer.disconnect()
      if (debounceTimer) clearTimeout(debounceTimer)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [step?.target_id, measureTarget])

  // Wake-from-pause: the instant the host un-pauses (user closed their
  // editing box), re-measure immediately so the ring reappears without
  // waiting for the next watch tick.
  useEffect(() => {
    if (!pausedByHost) measureTarget()
  }, [pausedByHost, measureTarget])

  useEffect(() => {
    if (!step) return
    let cleanup = () => {}

    switch (step.action) {
      case 'click': {
        const wantedId = step.target_id
        const handler = (e) => {
          const hit = e.target?.closest?.(`[data-tour-id="${wantedId}"]`)
          if (hit) onAdvance()
        }
        document.addEventListener('click', handler, true)
        cleanup = () => document.removeEventListener('click', handler, true)
        break
      }
      case 'input': {
        break
      }
      case 'focus': {
        if (!targetEl) break
        const handler = () => onAdvance()
        targetEl.addEventListener('focusin', handler, { once: true })
        cleanup = () => targetEl.removeEventListener('focusin', handler)
        break
      }
      case 'wait': {
        const ms = Number(step.wait_ms) > 0 ? Number(step.wait_ms) : 1500
        const t = setTimeout(() => onAdvance(), ms)
        cleanup = () => clearTimeout(t)
        break
      }
      default:
        break
    }
    return cleanup
  }, [targetEl, step, onAdvance])

  useEffect(() => {
    if (typeof window === 'undefined') return
    // Capture phase: read the pause state BEFORE bubble-phase handlers (e.g. a
    // host modal closing itself on this same Escape) can un-pause mid-keypress.
    // While the host has paused the tour, Escape belongs to the host's modals.
    const handler = (e) => {
      if (e.key === 'Escape' && document.body.dataset.ashrayaPaused !== 'true') onStop?.()
    }
    window.addEventListener('keydown', handler, true)
    return () => window.removeEventListener('keydown', handler, true)
  }, [onStop])

  if (!mounted || typeof window === 'undefined' || !step) return null

  const primary = theme?.primary || '#f97316'
  const showNextButton = step.action !== 'click'

  if (pausedByHost) {
    // Waiting card floats above the host's modal (sheets are z-40) but is not
    // hidden by the host's .ashraya-overlay pause CSS.
    return createPortal(
      <div className="ashraya-paused-card fixed left-1/2 top-4 z-[70] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 pointer-events-none">
        <div className="rounded-2xl border-2 bg-white px-4 py-3 shadow-2xl" style={{ borderColor: primary }}>
          <div className="text-xs font-semibold" style={{ color: primary }}>
            Guide paused
          </div>
          <div className="mt-1 text-sm text-zinc-800">
            👍 <strong>You're doing it!</strong> Make your changes here, then close this box — I'll show you what's next.
          </div>
        </div>
      </div>,
      document.body
    )
  }

  if (searching && !targetEl && !missing) {
    return createPortal(
      <div className="ashraya-overlay fixed inset-0 z-[60] pointer-events-none">
        <div className="absolute inset-0 bg-black/30" />
        <div
          className="absolute left-1/2 bottom-12 -translate-x-1/2 bg-white rounded-full shadow-2xl pl-3 pr-5 py-2.5 flex items-center gap-3 pointer-events-auto"
          onPointerDown={(e) => e.stopPropagation()}
          onPointerDownCapture={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onMouseDownCapture={(e) => e.stopPropagation()}
        >
          <div className="relative w-5 h-5">
            <div
              className="absolute inset-0 rounded-full border-2 border-transparent animate-spin"
              style={{ borderTopColor: primary, borderRightColor: primary }}
            />
          </div>
          <div className="text-sm text-slate-700">
            Locating the next step
            <span className="inline-block animate-pulse">…</span>
          </div>
          <button
            onClick={onStop}
            className="ml-2 text-xs text-slate-400 hover:text-slate-700 transition-colors"
            title="Stop tour"
          >
            Cancel
          </button>
        </div>
      </div>,
      document.body
    )
  }

  if (step?.target_id && !targetEl && !missing && !searching) {
    return createPortal(
      <div className="ashraya-overlay fixed inset-0 z-[60] pointer-events-none">
        <div className="absolute inset-0 bg-black/30" />
      </div>,
      document.body
    )
  }

  if (missing) {
    return createPortal(
      <div className="ashraya-overlay fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50">
        <div
          className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-5"
          onPointerDown={(e) => e.stopPropagation()}
          onPointerDownCapture={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onMouseDownCapture={(e) => e.stopPropagation()}
        >
          <div className="text-xs font-semibold mb-2" style={{ color: primary }}>
            Step {step.step_number} of {totalSteps}
          </div>
          <div
            className="text-base text-slate-800 mb-3"
            dangerouslySetInnerHTML={{ __html: renderInlineBold(step.fallback_text || step.instruction) }}
          />
          <div className="text-xs text-slate-500 mb-4 italic">
            We're waiting for that element to appear. If it doesn't show up,
            try following the instruction above manually, or click <strong>Skip</strong>
            to move on.
          </div>
          <div className="flex justify-between items-center">
            <button onClick={onStop} className="text-xs text-slate-500 hover:text-slate-800">
              Stop tour
            </button>
            <div className="flex gap-2">
              {onPrev && (
                <button onClick={onPrev} className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 hover:bg-slate-50">
                  ← Back
                </button>
              )}
              <button onClick={onSkip} className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 hover:bg-slate-50">
                Skip
              </button>
              {(() => {
                const isLastStep = step.step_number === totalSteps
                return (
                  <button
                    onClick={isLastStep ? onAdvance : undefined}
                    disabled={!isLastStep}
                    aria-disabled={!isLastStep}
                    title={isLastStep ? 'Finish tour' : 'Waiting for the target element to appear'}
                    className={`px-3 py-1.5 text-sm rounded-lg text-white font-medium ${isLastStep ? '' : 'opacity-40 cursor-not-allowed'}`}
                    style={{ backgroundColor: primary }}
                  >
                    {isLastStep ? 'Finish' : 'Next →'}
                  </button>
                )
              })()}
            </div>
          </div>
        </div>
      </div>,
      document.body
    )
  }

  if (!rect) return null

  const vw = window.innerWidth
  const vh = window.innerHeight

  let calloutTop
  let calloutLeft
  let calloutMaxH = null

  const targetIsInLeftSidebar = typeof step?.target_id === 'string'
    && step.target_id.startsWith('sidebar-')

  let targetDialogRect = null
  if (typeof document !== 'undefined' && step?.target_id) {
    const targetNode = document.querySelector(`[data-tour-id="${step.target_id}"]`)
    const dialogAncestor = targetNode?.closest?.('[role="dialog"]')
    if (dialogAncestor) {
      const dRect = dialogAncestor.getBoundingClientRect()
      if (dRect.width > 100 && dRect.height > 100) {
        targetDialogRect = dRect
      }
    }
  }

  const rectRight = rect.left + rect.width
  const hasRoomToRight = rectRight + CALLOUT_WIDTH + CALLOUT_GAP < vw - 16

  if (targetDialogRect) {
    const sideRoomRight = vw - targetDialogRect.right
    const sideRoomLeft = targetDialogRect.left
    const needed = CALLOUT_WIDTH + CALLOUT_GAP + 8

    if (sideRoomRight >= needed) {
      calloutLeft = targetDialogRect.right + CALLOUT_GAP
      calloutTop = Math.max(16, rect.top + rect.height / 2 - CALLOUT_HEIGHT_EST / 2)
    } else if (sideRoomLeft >= needed) {
      calloutLeft = targetDialogRect.left - CALLOUT_WIDTH - CALLOUT_GAP
      calloutTop = Math.max(16, rect.top + rect.height / 2 - CALLOUT_HEIGHT_EST / 2)
    } else {
      calloutLeft = Math.max(12, Math.min(targetDialogRect.left + (targetDialogRect.width - CALLOUT_WIDTH) / 2, vw - CALLOUT_WIDTH - 12))
      const belowDialog = targetDialogRect.top + targetDialogRect.height + CALLOUT_GAP
      const aboveDialog = targetDialogRect.top - CALLOUT_HEIGHT_EST - CALLOUT_GAP
      if (belowDialog + CALLOUT_HEIGHT_EST < vh - 16) {
        calloutTop = belowDialog
      } else if (aboveDialog > 16) {
        calloutTop = aboveDialog
      } else {
        calloutTop = Math.max(16, targetDialogRect.top + (targetDialogRect.height - CALLOUT_HEIGHT_EST) / 2)
      }
    }
  } else if (targetIsInLeftSidebar && hasRoomToRight) {
    calloutTop = Math.max(16, rect.top + rect.height / 2 - CALLOUT_HEIGHT_EST / 2)
    calloutLeft = rectRight + CALLOUT_GAP
  } else {
    calloutTop = rect.top + rect.height + CALLOUT_GAP
    calloutLeft = rect.left + rect.width / 2 - CALLOUT_WIDTH / 2
    if (calloutTop + CALLOUT_HEIGHT_EST > vh - 16) {
      if (rect.top - CALLOUT_HEIGHT_EST - CALLOUT_GAP > 16) {
        calloutTop = rect.top - CALLOUT_HEIGHT_EST - CALLOUT_GAP
      } else if (rectRight + CALLOUT_WIDTH + CALLOUT_GAP < vw - 16) {
        calloutTop = Math.max(16, rect.top + rect.height / 2 - CALLOUT_HEIGHT_EST / 2)
        calloutLeft = rectRight + CALLOUT_GAP
      } else {
        calloutTop = Math.max(16, rect.top + rect.height / 2 - CALLOUT_HEIGHT_EST / 2)
        calloutLeft = Math.max(16, rect.left - CALLOUT_WIDTH - CALLOUT_GAP)
      }
    }
  }
  calloutLeft = Math.max(12, Math.min(calloutLeft, vw - CALLOUT_WIDTH - 12))
  calloutTop  = Math.max(12, Math.min(calloutTop, vh - CALLOUT_HEIGHT_EST - 12))

  // Never cover the target itself — unconditional (11 Aug fix; the old code
  // gave up when neither full-height band fit, which is exactly what happens
  // on phones, leaving the bubble ON the box it was describing). Order:
  // below → above → right → left → shrink into the larger band (the bubble
  // body scrolls) → giant-target last resort: pin to the bottom edge so the
  // top of the target — where forms start — stays visible.
  const tTop = rect.top - PAD
  const tBottom = rect.top + rect.height + PAD
  const overlapsTarget =
    calloutTop < tBottom &&
    calloutTop + CALLOUT_HEIGHT_EST > tTop &&
    calloutLeft < rect.left + rect.width + PAD &&
    calloutLeft + CALLOUT_WIDTH > rect.left - PAD
  if (overlapsTarget) {
    const roomAbove = tTop - 12
    const roomBelow = vh - tBottom - 12
    const roomRight = vw - (rect.left + rect.width + PAD) - 12
    const roomLeft = rect.left - PAD - 12
    if (roomBelow >= CALLOUT_HEIGHT_EST + CALLOUT_GAP) {
      calloutTop = tBottom + CALLOUT_GAP
    } else if (roomAbove >= CALLOUT_HEIGHT_EST + CALLOUT_GAP) {
      calloutTop = tTop - CALLOUT_HEIGHT_EST - CALLOUT_GAP
    } else if (roomRight >= CALLOUT_WIDTH + CALLOUT_GAP) {
      calloutLeft = rect.left + rect.width + PAD + CALLOUT_GAP
      calloutTop = Math.max(12, Math.min(rect.top + rect.height / 2 - CALLOUT_HEIGHT_EST / 2, vh - CALLOUT_HEIGHT_EST - 12))
    } else if (roomLeft >= CALLOUT_WIDTH + CALLOUT_GAP) {
      calloutLeft = Math.max(12, rect.left - PAD - CALLOUT_GAP - CALLOUT_WIDTH)
      calloutTop = Math.max(12, Math.min(rect.top + rect.height / 2 - CALLOUT_HEIGHT_EST / 2, vh - CALLOUT_HEIGHT_EST - 12))
    } else if (Math.max(roomAbove, roomBelow) >= 180) {
      calloutMaxH = Math.max(roomAbove, roomBelow) - CALLOUT_GAP
      if (roomBelow >= roomAbove) {
        calloutTop = tBottom + CALLOUT_GAP
      } else {
        calloutTop = Math.max(12, tTop - calloutMaxH - CALLOUT_GAP)
      }
    } else {
      calloutMaxH = Math.round(vh * 0.45)
      calloutTop = vh - calloutMaxH - 12
    }
  }

  return createPortal(
    <div className="ashraya-overlay">
      <div
        aria-hidden="true"
        className="fixed pointer-events-none transition-all duration-150 ease-out"
        style={{
          top: rect.top - PAD,
          left: rect.left - PAD,
          width: rect.width + PAD * 2,
          height: rect.height + PAD * 2,
          borderRadius: 10,
          boxShadow: `0 0 0 9999px rgba(0, 0, 0, 0.55), 0 0 0 3px ${primary}, 0 0 24px 4px ${primary}66`,
          zIndex: 60,
        }}
      />

      {/* Interaction fence: while a step is showing, everything OUTSIDE the
          spotlight hole is unclickable — it's a guided moment, not free play.
          The hole itself stays interactive only when the step needs a click
          or typing (action 'click', or allow_interaction on the step).
          EXCEPTION: an observe/input step with allow_interaction is a "work
          here" step — the user is invited to open and edit things anywhere,
          so no fence at all; the tooltip simply waits with Next. */}
      {!(step.action !== 'click' && step.allow_interaction === true) && (() => {
        const holeTop = rect.top - PAD
        const holeLeft = rect.left - PAD
        const holeW = rect.width + PAD * 2
        const holeH = rect.height + PAD * 2
        const interactive = step.action === 'click' || step.allow_interaction === true
        const b = { position: 'fixed', zIndex: 61, pointerEvents: 'auto', background: 'transparent' }
        return (
          <div aria-hidden="true" data-testid="ashraya-click-fence">
            <div style={{ ...b, top: 0, left: 0, right: 0, height: Math.max(0, holeTop) }} />
            <div style={{ ...b, top: holeTop + holeH, left: 0, right: 0, bottom: 0 }} />
            <div style={{ ...b, top: holeTop, left: 0, width: Math.max(0, holeLeft), height: holeH }} />
            <div style={{ ...b, top: holeTop, left: holeLeft + holeW, right: 0, height: holeH }} />
            {!interactive && (
              <div style={{ ...b, top: holeTop, left: holeLeft, width: holeW, height: holeH }} />
            )}
          </div>
        )
      })()}

      <div
        role="dialog"
        aria-label={`Step ${step.step_number} of ${totalSteps}`}
        className="fixed pointer-events-auto bg-white rounded-2xl shadow-2xl border border-slate-200 p-4 flex flex-col"
        onPointerDown={(e) => e.stopPropagation()}
        onPointerDownCapture={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseDownCapture={(e) => e.stopPropagation()}
        style={{
          top: calloutTop,
          left: calloutLeft,
          width: CALLOUT_WIDTH,
          maxHeight: calloutMaxH != null ? calloutMaxH : 'calc(100vh - 24px)',
          zIndex: 65,
        }}
      >
        <div className="flex items-center justify-between mb-2 flex-shrink-0">
          <div className="text-xs font-semibold" style={{ color: primary }}>
            Step {step.step_number} of {totalSteps}
          </div>
          <button
            onClick={onStop}
            className="text-slate-400 hover:text-slate-700 text-base leading-none"
            aria-label="Stop tour"
            title="Stop tour (Esc)"
          >
            ✕
          </button>
        </div>
        <div
          className="text-sm text-slate-800 mb-3 leading-relaxed overflow-y-auto flex-1 min-h-0 pr-1"
          dangerouslySetInnerHTML={{ __html: renderInlineBold(step.instruction) }}
        />
        <div className="flex items-center justify-between gap-2 flex-shrink-0">
          <div className="flex gap-1">
            {onPrev && (
              <button
                onClick={onPrev}
                className="text-xs px-2 py-1 rounded text-slate-600 hover:bg-slate-100"
              >
                ← Back
              </button>
            )}
            <button
              onClick={onSkip}
              className="text-xs px-2 py-1 rounded text-slate-500 hover:bg-slate-100"
            >
              Skip
            </button>
          </div>
          {showNextButton && (
            <button
              onClick={onAdvance}
              className="px-3 py-1.5 text-sm rounded-lg text-white font-medium shadow-sm"
              style={{ backgroundColor: primary }}
            >
              {step.step_number === totalSteps ? 'Finish' : 'Next →'}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}

// Minimal markdown renderer — bold only. HTML-escapes input first.
function renderInlineBold(text = '') {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\n/g, '<br/>')
}
